import { supabase } from './supabase';
import { getUserChurch } from './auth';

/**
 * Registra uma ação do usuário para análise
 * @param action Nome da ação
 * @param details Detalhes adicionais
 * @returns Promise<boolean> Sucesso ou falha
 */
export async function trackUserAction(action: string, details: any = {}): Promise<boolean> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return false;

    // Obter igreja_id do usuário
    const churchId = await getUserChurch();
    
    // Registrar ação no log
    const { error } = await supabase
      .from('user_actions_log')
      .insert([{
        user_id: session.user.id,
        action,
        details,
        igreja_id: churchId,
        ip_address: '0.0.0.0', // Em um ambiente real, você capturaria o IP
        user_agent: navigator.userAgent
      }]);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error tracking user action:', error);
    return false;
  }
}

/**
 * Registra o acesso a uma página
 * @param pageName Nome da página
 * @param additionalData Dados adicionais
 */
export async function trackPageView(pageName: string, additionalData: any = {}): Promise<void> {
  try {
    await trackUserAction('page_view', {
      page: pageName,
      url: window.location.pathname,
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
}

/**
 * Registra interação com conteúdo
 * @param contentType Tipo de conteúdo
 * @param contentId ID do conteúdo
 * @param action Ação realizada
 */
export async function trackContentInteraction(
  contentType: 'course' | 'bible' | 'prayer' | 'testimony' | 'quiz' | 'group' | 'ministry',
  contentId: string,
  action: 'view' | 'create' | 'update' | 'delete' | 'complete'
): Promise<void> {
  try {
    await trackUserAction('content_interaction', {
      content_type: contentType,
      content_id: contentId,
      action,
      timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error tracking content interaction:', error);
  }
}

/**
 * Registra progresso em curso
 * @param courseId ID do curso
 * @param lessonId ID da aula
 * @param progress Porcentagem de progresso
 */
export async function trackCourseProgress(
  courseId: string,
  lessonId: string,
  progress: number
): Promise<void> {
  try {
    await trackUserAction('course_progress', {
      course_id: courseId,
      lesson_id: lessonId,
      progress,
      timestamp: new Date().toISOString()
    });
    
    // Atualizar progresso no banco de dados
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    
    // Verificar se já existe um registro de progresso
    const { data: existingProgress } = await supabase
      .from('course_progress')
      .select('*')
      .eq('user_id', session.user.id)
      .eq('course_id', courseId)
      .single();
    
    if (existingProgress) {
      // Atualizar progresso existente
      const lessonsCompleted = existingProgress.lessons_completed || [];
      if (!lessonsCompleted.includes(lessonId)) {
        lessonsCompleted.push(lessonId);
      }
      
      await supabase
        .from('course_progress')
        .update({
          lessons_completed: lessonsCompleted,
          updated_at: new Date().toISOString()
        })
        .eq('id', existingProgress.id);
    } else {
      // Criar novo registro de progresso
      await supabase
        .from('course_progress')
        .insert([{
          user_id: session.user.id,
          course_id: courseId,
          lessons_completed: [lessonId],
          is_completed: false
        }]);
    }
  } catch (error) {
    console.error('Error tracking course progress:', error);
  }
}

/**
 * Registra resultado de quiz
 * @param quizId ID do quiz
 * @param score Pontuação
 * @param totalQuestions Total de questões
 */
export async function trackQuizResult(
  quizId: string,
  score: number,
  totalQuestions: number
): Promise<void> {
  try {
    await trackUserAction('quiz_result', {
      quiz_id: quizId,
      score,
      total_questions: totalQuestions,
      percentage: Math.round((score / totalQuestions) * 100),
      timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error tracking quiz result:', error);
  }
}

/**
 * Registra atividade de oração
 * @param prayerId ID do pedido de oração
 * @param action Ação realizada
 */
export async function trackPrayerActivity(
  prayerId: string,
  action: 'create' | 'update' | 'delete'
): Promise<void> {
  try {
    await trackUserAction('prayer_activity', {
      prayer_id: prayerId,
      action,
      timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error tracking prayer activity:', error);
  }
}

/**
 * Registra atividade de estudo bíblico
 * @param bookId ID do livro
 * @param chapter Capítulo
 * @param verse Versículo (opcional)
 * @param action Ação realizada
 */
export async function trackBibleActivity(
  bookId: string,
  chapter: number,
  verse: number | null,
  action: 'read' | 'bookmark' | 'note' | 'share'
): Promise<void> {
  try {
    await trackUserAction('bible_activity', {
      book_id: bookId,
      chapter,
      verse,
      action,
      timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error tracking bible activity:', error);
  }
}

/**
 * Registra atividade de grupo
 * @param groupId ID do grupo
 * @param action Ação realizada
 */
export async function trackGroupActivity(
  groupId: string,
  action: 'view' | 'join' | 'leave'
): Promise<void> {
  try {
    await trackUserAction('group_activity', {
      group_id: groupId,
      action,
      timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error tracking group activity:', error);
  }
}

/**
 * Registra atividade de ministério
 * @param ministryId ID do ministério
 * @param action Ação realizada
 */
export async function trackMinistryActivity(
  ministryId: string,
  action: 'view' | 'join' | 'leave'
): Promise<void> {
  try {
    await trackUserAction('ministry_activity', {
      ministry_id: ministryId,
      action,
      timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error tracking ministry activity:', error);
  }
}

/**
 * Registra atividade de notificação
 * @param notificationId ID da notificação
 * @param action Ação realizada
 */
export async function trackNotificationActivity(
  notificationId: string,
  action: 'view' | 'click' | 'dismiss'
): Promise<void> {
  try {
    await trackUserAction('notification_activity', {
      notification_id: notificationId,
      action,
      timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error tracking notification activity:', error);
  }
}